import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { api, type BillsView, type ClientPortal } from "../lib/api";
import { useResource, useAction } from "../lib/useResource";
import {
  Button,
  Empty,
  Failed,
  Mark,
  SkeletonRows,
} from "../components/primitives";
import { day, money, relativeDay } from "../lib/format";

/**
 * The client portal's pages.
 *
 * **Why these are pages now and not sections.** The portal was one long
 * document with a sidebar of anchor links — `#actions`, `#progress`,
 * `#deliverables`, `#files`, `#invoices`. Three things were wrong with that,
 * and only the first is cosmetic:
 *
 *   1. On a phone the sidebar was `hidden lg:block`, so a client on the device
 *      they actually read email on had **no navigation at all** — just a long
 *      scroll. `PRODUCT.md` says neither client nor consultant may be assumed
 *      to have a large screen.
 *   2. **Two of the five anchors pointed at the wrong content.**
 *      `id="deliverables"` wrapped the *project timeline*, which is progress;
 *      `id="files"` wrapped approved deliverables and documents together, so
 *      "Deliverables" and "Files" were one list under the wrong heading and a
 *      heading with no list. Splitting them is what makes the five names true.
 *   3. An anchor is not a place. A client cannot be sent a link to their
 *      invoices, cannot bookmark them, and the back button does nothing.
 *
 * **What each page shows that the overview does not.** The overview is a
 * summary and stays one: it caps every list and links through. The pages are
 * the whole of each thing — and for Actions that meant the server had to start
 * returning what the client has already dealt with (`pastActions`), because an
 * actions page that only lists what is outstanding is a to-do list rather than
 * a record, and cannot answer "did I not already send that?".
 *
 * Nothing here decides what a client may see. Everything on these pages
 * arrived in one payload that `clientPortal` built through the client's own
 * policies — classification and publication both — so there is no "internal"
 * anything to keep off a tab.
 */

type Portal = ClientPortal;

/** How many rows the overview shows before it defers to the page. */
const OVERVIEW_CAP = 4;

function PageHead({ title, sub }: { title: string; sub?: string }) {
  return (
    <header className="border-b border-line-strong pb-6">
      <h1 className="font-editorial text-[clamp(2.1rem,4vw,3.4rem)] font-medium leading-[1.02] tracking-[-0.035em]">
        {title}
      </h1>
      {sub && <p className="mt-3 max-w-[58ch] text-md text-secondary">{sub}</p>}
    </header>
  );
}

/** "See all 9" — only when there is more than the overview showed. */
function SeeAll({ to, count, what }: { to: string; count: number; what: string }) {
  if (count <= OVERVIEW_CAP) return null;
  return (
    <Link
      to={to}
      className="mt-4 inline-block text-base text-secondary underline-offset-4 hover:text-ink hover:underline"
    >
      See all {count} {what} &rarr;
    </Link>
  );
}

// ================================================================ overview ==

/**
 * Where this is, what is needed, what can be taken away — in that order.
 *
 * Kept close to what it was, because it reads well and the client liked it.
 * The one change of substance is that every list is now capped and links
 * through: an overview that shows everything is not an overview, and it was
 * leaving the five pages with nothing to be.
 */
export function PortalOverview({
  engagementId,
  portal,
  base,
  respond,
  reload,
}: {
  engagementId: string;
  portal: Portal;
  /** `/e/<id>`, so links are built once rather than in every page. */
  base: string;
  respond: ReturnType<typeof useAction<[string]>>;
  reload: () => void;
}) {
  const { snapshot, actionCentre, journey, documents, approvedDeliverables } =
    portal;
  const needsYou =
    actionCentre.openActions.length + actionCentre.awaitingYourApproval.length;
  const milestones =
    journey.completed.length + journey.active.length + journey.upcoming.length;

  return (
    <>
      <section className="max-w-[72rem]">
        <p className="label">{snapshot.reference}</p>
        <h1 className="mt-5 font-editorial text-[clamp(2.8rem,5.4vw,5.2rem)] font-medium leading-[0.95] tracking-[-0.04em]">
          Here&apos;s where your engagement stands.
        </h1>
        {snapshot.leadPartner && (
          <p className="mt-6 text-md text-secondary">
            {snapshot.engagement} &middot; Your A&amp;C lead: {snapshot.leadPartner}
          </p>
        )}
      </section>

      {/* ---- progress, stated as a sentence and a rule ---- */}
      <section className="mt-10 border-t border-line-strong pt-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <span data-numeric className="font-display text-3xl font-medium tracking-tight">
              {snapshot.progress}%
            </span>
            <span className="label ml-3">complete</span>
          </div>

          {snapshot.nextMilestone && (
            <p className="text-base text-secondary">
              Next: {snapshot.nextMilestone.name}
              {snapshot.nextMilestone.dueOn && (
                <> &middot; {day(snapshot.nextMilestone.dueOn)}</>
              )}
            </p>
          )}

          {snapshot.targetCompletion && (
            <p className="text-base text-secondary">
              Expected completion {day(snapshot.targetCompletion)}
            </p>
          )}
        </div>

        <ProgressRule value={snapshot.progress} />

        {milestones > 0 && (
          <Link
            to={`${base}/progress`}
            className="mt-4 inline-block text-base text-secondary underline-offset-4 hover:text-ink hover:underline"
          >
            See the full timeline &rarr;
          </Link>
        )}
      </section>

      {/* ---- what we need from you ---- */}
      <section className="mt-12">
        <div className="flex items-baseline justify-between gap-4 border-t border-line-strong pt-5">
          <h2 className="font-display text-xl font-medium tracking-title">
            {needsYou > 0 ? "Things we need from you" : "Nothing needs you"}
          </h2>
          {needsYou > 0 && (
            <span data-numeric className="label">
              {needsYou}
            </span>
          )}
        </div>

        {respond.refusal && (
          <p role="alert" className="mt-4 border border-danger/40 bg-danger/[0.08] px-3 py-2 text-xs text-danger">
            {respond.refusal.message}
          </p>
        )}

        {needsYou === 0 ? (
          <Empty>
            When your team needs a decision, a document or a sign-off, it will
            appear here and you will be emailed.
          </Empty>
        ) : (
          <div className="mt-2">
            {actionCentre.awaitingYourApproval.slice(0, OVERVIEW_CAP).map((item) => (
              <Approval key={item.versionId} item={item} onDone={reload} />
            ))}
            {actionCentre.openActions
              .slice(0, Math.max(0, OVERVIEW_CAP - actionCentre.awaitingYourApproval.length))
              .map((action) => (
                <OpenAction
                  key={action.id}
                  action={action}
                  respond={respond}
                  reload={reload}
                />
              ))}
          </div>
        )}

        <SeeAll to={`${base}/actions`} count={needsYou} what="open items" />
      </section>

      {/* ---- what you can take away ---- */}
      <section className="mt-12">
        <h2 className="border-t border-line-strong pt-5 font-display text-xl font-medium tracking-title">
          Released to you
        </h2>

        <div className="mt-2">
          {approvedDeliverables.slice(0, OVERVIEW_CAP).map((item) => (
            <DeliverableRow key={item.versionId} item={item} />
          ))}
        </div>

        {approvedDeliverables.length + documents.length === 0 ? (
          <Empty title="Nothing released yet">
            Documents appear here once your team releases them to you. Anything
            still in draft stays with the firm.
          </Empty>
        ) : (
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2">
            <Link
              to={`${base}/deliverables`}
              className="text-base text-secondary underline-offset-4 hover:text-ink hover:underline"
            >
              {approvedDeliverables.length} deliverable
              {approvedDeliverables.length === 1 ? "" : "s"} &rarr;
            </Link>
            <Link
              to={`${base}/files`}
              className="text-base text-secondary underline-offset-4 hover:text-ink hover:underline"
            >
              {documents.length} file{documents.length === 1 ? "" : "s"} &rarr;
            </Link>
          </div>
        )}
      </section>

      {/* ---- what you owe ---- */}
      <InvoiceSummary engagementId={engagementId} base={base} />
    </>
  );
}

function ProgressRule({ value }: { value: number }) {
  return (
    <div className="mt-5 h-[3px] w-full bg-ink/15">
      <motion.div
        className="h-full bg-sand"
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      />
    </div>
  );
}

// ================================================================= actions ==

/**
 * Everything waiting on the client, and everything already dealt with.
 *
 * The second half is the reason this is a page. `pastActions` was added to the
 * payload for it: a client who cannot see what they already sent has to ask
 * the firm, which is the phone call the portal exists to prevent.
 */
export function PortalActions({
  portal,
  respond,
  reload,
}: {
  portal: Portal;
  respond: ReturnType<typeof useAction<[string]>>;
  reload: () => void;
}) {
  const { openActions, awaitingYourApproval, pastActions } = portal.actionCentre;
  const needsYou = openActions.length + awaitingYourApproval.length;

  return (
    <>
      <PageHead
        title="Actions"
        sub="What your team needs from you, and what you have already dealt with. You are emailed whenever something new arrives here."
      />

      {respond.refusal && (
        <p role="alert" className="mt-6 border border-danger/40 bg-danger/[0.08] px-3 py-2 text-xs text-danger">
          {respond.refusal.message}
        </p>
      )}

      <section className="mt-9">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="font-display text-xl font-medium tracking-title">
            Waiting on you
          </h2>
          {needsYou > 0 && (
            <span data-numeric className="label">
              {needsYou}
            </span>
          )}
        </div>

        {needsYou === 0 ? (
          <Empty title="Nothing needs you">
            When your team needs a decision, a document or a sign-off, it will
            appear here.
          </Empty>
        ) : (
          <div className="mt-2">
            {awaitingYourApproval.map((item) => (
              <Approval key={item.versionId} item={item} onDone={reload} />
            ))}
            {openActions.map((action) => (
              <OpenAction
                key={action.id}
                action={action}
                respond={respond}
                reload={reload}
              />
            ))}
          </div>
        )}
      </section>

      <section className="mt-12">
        <h2 className="border-t border-line-strong pt-5 font-display text-xl font-medium tracking-title">
          Already dealt with
        </h2>

        {pastActions.length === 0 ? (
          <Empty>
            Once you have answered something, it stays here as a record of when.
          </Empty>
        ) : (
          <ul className="mt-2">
            {pastActions.map((a) => (
              <li
                key={a.id}
                className="register-entry flex flex-wrap items-center justify-between gap-4 py-4"
              >
                <span className="min-w-0">
                  <span className="block text-md text-secondary">{a.title}</span>
                  <span className="mt-1 block text-xs text-muted">
                    {a.respondedAt
                      ? `Answered ${day(a.respondedAt)}`
                      : /* 'closed' without a response: the firm withdrew it or
                           it lapsed. Saying "answered" would be a small lie
                           about something the client is being shown as theirs. */
                        "Closed by your team"}
                    {a.dueOn && ` · was due ${day(a.dueOn)}`}
                  </span>
                </span>
                <Mark tone="spent">Done</Mark>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function OpenAction({
  action,
  respond,
  reload,
}: {
  action: Portal["actionCentre"]["openActions"][number];
  respond: ReturnType<typeof useAction<[string]>>;
  reload: () => void;
}) {
  return (
    <div className="register-entry flex flex-wrap items-center justify-between gap-4 py-5">
      <div className="min-w-0">
        <p className="text-md text-ink">{action.title}</p>
        <p className="mt-1 text-xs text-muted">
          {action.dueOn ? (
            <>
              Due {day(action.dueOn)} &middot; {relativeDay(action.dueOn)}
            </>
          ) : (
            "No date set"
          )}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {action.overdue && <Mark tone="danger">Overdue</Mark>}
        <Button
          tone="secondary"
          loading={respond.pending}
          onClick={() => void respond.invoke(action.id).then((ok) => ok && reload())}
        >
          Review action
        </Button>
      </div>
    </div>
  );
}

// ================================================================ progress ==

/**
 * Where the engagement stands, and the milestones behind that number.
 *
 * This is what `#deliverables` used to point at, which is why the labels were
 * confusing: a timeline of milestones is progress, not a list of work products.
 */
export function PortalProgress({ portal }: { portal: Portal }) {
  const { snapshot, journey } = portal;
  const total =
    journey.completed.length + journey.active.length + journey.upcoming.length;

  return (
    <>
      <PageHead
        title="Progress"
        sub="How far the engagement has come, and what is next. The percentage is calculated from completed milestones, not estimated."
      />

      <section className="mt-9">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <span data-numeric className="font-display text-4xl font-medium tracking-tight">
              {snapshot.progress}%
            </span>
            <span className="label ml-3">complete</span>
          </div>
          <dl className="flex flex-wrap gap-x-10 gap-y-3">
            {snapshot.nextMilestone && (
              <div>
                <dt className="label">Next milestone</dt>
                <dd className="mt-1 text-base text-ink">
                  {snapshot.nextMilestone.name}
                  {snapshot.nextMilestone.dueOn && (
                    <span className="text-secondary">
                      {" · "}
                      {day(snapshot.nextMilestone.dueOn)}
                    </span>
                  )}
                </dd>
              </div>
            )}
            {snapshot.targetCompletion && (
              <div>
                <dt className="label">Expected completion</dt>
                <dd className="mt-1 text-base text-ink">
                  {day(snapshot.targetCompletion)}
                </dd>
              </div>
            )}
          </dl>
        </div>
        <ProgressRule value={snapshot.progress} />
      </section>

      <section className="mt-12">
        <h2 className="border-t border-line-strong pt-5 font-display text-xl font-medium tracking-title">
          The timeline
        </h2>

        {total === 0 ? (
          <Empty title="The journey is being set out">
            Your team is still laying out the milestones for this engagement.
          </Empty>
        ) : (
          <ol className="mt-2">
            {journey.completed.map((m) => (
              <li key={m.name} className="register-entry flex flex-wrap items-center justify-between gap-4 py-4">
                <span className="text-base text-muted line-through">{m.name}</span>
                <span className="flex items-center gap-3">
                  <span className="text-xs text-muted">{day(m.completedAt)}</span>
                  <Mark tone="success">Complete</Mark>
                </span>
              </li>
            ))}
            {journey.active.map((m) => (
              <li key={m.name} className="register-entry flex flex-wrap items-center justify-between gap-4 py-4">
                <span className="text-base text-ink">{m.name}</span>
                <span className="flex items-center gap-3">
                  <span className="text-xs text-secondary">{day(m.dueOn)}</span>
                  <Mark tone="live">In progress</Mark>
                </span>
              </li>
            ))}
            {journey.upcoming.map((m) => (
              <li key={m.name} className="register-entry flex flex-wrap items-center justify-between gap-4 py-4">
                <span className="text-base text-secondary">{m.name}</span>
                <span className="flex items-center gap-3">
                  <span className="text-xs text-muted">{day(m.dueOn)}</span>
                  <Mark>Upcoming</Mark>
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>
    </>
  );
}

// ============================================================ deliverables ==

/**
 * The work products, separated from the documents they used to be mixed with.
 *
 * A deliverable is the thing the engagement exists to produce and it carries a
 * version and a sign-off; a file is anything else released along the way. They
 * were one list under the heading "Files shared with you", which made the
 * version numbers look like filing noise rather than the record of what was
 * approved.
 */
export function PortalDeliverables({
  portal,
  reload,
}: {
  portal: Portal;
  reload: () => void;
}) {
  const { approvedDeliverables, actionCentre } = portal;

  return (
    <>
      <PageHead
        title="Deliverables"
        sub="The work your engagement produces. Each one names an exact version — the one you approved, not whatever came after it."
      />

      {actionCentre.awaitingYourApproval.length > 0 && (
        <section className="mt-9">
          <h2 className="font-display text-xl font-medium tracking-title">
            Waiting for your approval
          </h2>
          <div className="mt-2">
            {actionCentre.awaitingYourApproval.map((item) => (
              <Approval key={item.versionId} item={item} onDone={reload} />
            ))}
          </div>
        </section>
      )}

      <section className="mt-12">
        <h2 className="border-t border-line-strong pt-5 font-display text-xl font-medium tracking-title">
          Approved
        </h2>

        {approvedDeliverables.length === 0 ? (
          <Empty title="Nothing approved yet">
            A deliverable appears here once it has been signed off. Until then
            it stays with the firm.
          </Empty>
        ) : (
          <div className="mt-2">
            {approvedDeliverables.map((item) => (
              <DeliverableRow key={item.versionId} item={item} showDate />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function DeliverableRow({
  item,
  showDate = false,
}: {
  item: Portal["approvedDeliverables"][number];
  showDate?: boolean;
}) {
  return (
    <div className="register-entry flex flex-wrap items-center justify-between gap-4 py-4">
      <span className="min-w-0">
        <span className="block truncate text-base text-ink">{item.title}</span>
        {showDate && (
          <span className="mt-1 block text-xs text-muted">
            {item.approvedAt
              ? `Approved ${day(item.approvedAt)}`
              : /* Approved by the firm without the client being asked, which
                   is a real path — `requires_client_approval` is per
                   deliverable. Claiming a date there would invent one. */
                "Released by your team"}
          </span>
        )}
      </span>
      <span className="flex shrink-0 items-center gap-3">
        <span className="font-mono text-xs text-muted">v{item.versionNo}</span>
        <Download
          path={`/api/deliverable-versions/${item.versionId}/download`}
          label={item.title}
        />
      </span>
    </div>
  );
}

// =================================================================== files ==

/**
 * Everything else released, grouped by the folder it was filed in.
 *
 * The firm files every engagement into the same eight folders, and the client
 * sees whichever of them hold something released. Grouping is not decoration:
 * "research" and "commercial" answer different questions, and a flat list of
 * thirty filenames makes the client read all thirty to find one.
 */
export function PortalFiles({ portal }: { portal: Portal }) {
  const { documents } = portal;

  // Preserve the server's order (it sorts by folder then name) rather than
  // imposing one here, so the grouping follows the firm's filing convention.
  const folders: Array<[string, typeof documents]> = [];
  for (const doc of documents) {
    const last = folders[folders.length - 1];
    if (last && last[0] === doc.folder) last[1].push(doc);
    else folders.push([doc.folder, [doc]]);
  }

  return (
    <>
      <PageHead
        title="Files"
        sub="Documents your team has released to you. Anything still in draft stays with the firm until it is ready."
      />

      {documents.length === 0 ? (
        <div className="mt-9">
          <Empty title="Nothing released yet">
            Files appear here once your team releases them. You are emailed when
            something arrives.
          </Empty>
        </div>
      ) : (
        folders.map(([folder, docs]) => (
          <section key={folder} className="mt-10">
            <h2 className="border-t border-line-strong pt-5 label">{folder}</h2>
            <div className="mt-2">
              {docs.map((doc) => (
                <div
                  key={doc.id}
                  className="register-entry flex flex-wrap items-center justify-between gap-4 py-4"
                >
                  <span className="min-w-0 truncate text-base text-ink">
                    {doc.name}
                  </span>
                  {/* A document with no version has no bytes to fetch. Saying
                      so is better than a button that can only fail. */}
                  {doc.latestVersionId ? (
                    <Download
                      path={`/api/document-versions/${doc.latestVersionId}/download`}
                      label={doc.name}
                    />
                  ) : (
                    <span className="shrink-0 text-xs text-muted">
                      Not yet uploaded
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))
      )}
    </>
  );
}

// ================================================================ invoices ==

/**
 * The client's side of Bills.
 *
 * Only invoices that have been **sent** reach here — `invoice_read` in
 * migration 033 refuses a client any invoice still in draft. That matters more
 * than it looks: a draft is the firm's working document, and showing one would
 * turn an internal estimate into an apparent demand for payment.
 *
 * Read-only, deliberately. A client does not record their own payment; the firm
 * records it when the money arrives, so the register says what the firm has
 * actually received rather than what somebody says they sent.
 */
export function PortalInvoices({ engagementId }: { engagementId: string }) {
  const bills = useResource<BillsView>(
    () => api.get<BillsView>(`/api/engagements/${engagementId}/bills`),
    [engagementId],
  );

  const view = bills.resource.state === "ready" ? bills.resource.data : undefined;
  const invoices = view?.invoices ?? [];

  return (
    <>
      <PageHead
        title="Invoices"
        sub="What has been billed and what has been received. Only invoices your team has issued appear here."
      />

      {bills.resource.state === "loading" && (
        <div className="mt-9">
          <SkeletonRows rows={3} />
        </div>
      )}
      {bills.resource.state === "failed" && (
        <Failed
          message={bills.resource.message}
          correlationId={bills.resource.correlationId}
        />
      )}

      {view && invoices.length === 0 && (
        <div className="mt-9">
          <Empty title="Nothing to pay">
            Invoices appear here when the firm issues them.
          </Empty>
        </div>
      )}

      {view && invoices.length > 0 && (
        <>
          <dl className="mt-9 flex flex-wrap gap-x-10 gap-y-3">
            <div>
              <dt className="label">Billed</dt>
              <dd data-numeric className="mt-1 text-xl text-secondary">
                {money(view.totals.billed, view.currency)}
              </dd>
            </div>
            <div>
              <dt className="label">Received</dt>
              <dd data-numeric className="mt-1 text-xl text-secondary">
                {money(view.totals.paid, view.currency)}
              </dd>
            </div>
            <div>
              <dt className="label">Outstanding</dt>
              <dd
                data-numeric
                className={`mt-1 text-xl ${view.totals.outstanding > 0 ? "text-ink" : "text-secondary"}`}
              >
                {money(view.totals.outstanding, view.currency)}
              </dd>
            </div>
            {view.totals.overdue > 0 && (
              <div>
                <dt className="label">Overdue</dt>
                <dd data-numeric className="mt-1 text-xl text-danger">
                  {money(view.totals.overdue, view.currency)}
                </dd>
              </div>
            )}
          </dl>

          <div className="mt-8">
            {invoices.map((inv) => {
              const settled = inv.outstanding <= 0;
              return (
                <div key={inv.id} className="register-entry py-5">
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <div className="min-w-0">
                      <span className="font-mono text-sm text-ink">
                        {inv.reference}
                      </span>
                      <p className="mt-1 text-sm text-secondary">
                        {inv.issuedOn && `Issued ${day(inv.issuedOn)}`}
                        {inv.dueOn && (
                          <span className={inv.overdue ? "text-danger" : undefined}>
                            {" · "}
                            {inv.overdue ? "was due " : "due "}
                            {day(inv.dueOn)}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p data-numeric className="text-lg text-ink">
                        {money(inv.total, inv.currency)}
                      </p>
                      <p data-numeric className="text-sm text-secondary">
                        {settled
                          ? "paid in full"
                          : `${money(inv.outstanding, inv.currency)} outstanding`}
                      </p>
                    </div>
                  </div>

                  <ul className="mt-3 text-sm text-secondary">
                    {inv.lines.map((l) => (
                      <li key={l.id} className="flex justify-between gap-4 py-0.5">
                        <span>{l.description}</span>
                        <span data-numeric>{money(l.amount, inv.currency)}</span>
                      </li>
                    ))}
                  </ul>

                  {inv.payments.length > 0 && (
                    <ul className="mt-3 border-t border-line pt-2 text-sm text-muted">
                      {inv.payments.map((pm) => (
                        <li key={pm.id} className="flex justify-between gap-4 py-0.5">
                          <span>
                            Received {day(pm.paidOn)}
                            {pm.reference && ` · ${pm.reference}`}
                          </span>
                          <span data-numeric>{money(pm.amount, inv.currency)}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {inv.notes && (
                    <p className="mt-3 max-w-[62ch] whitespace-pre-line text-sm text-secondary">
                      {inv.notes}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

/**
 * The overview's two numbers, which is all an overview owes an invoice.
 *
 * Its own fetch, because invoices come from `/api/engagements/:id/bills` and
 * not from the portal payload. A failure here is silent rather than loud: the
 * client's outstanding balance not loading should not put an error banner
 * across the page that told them where their engagement stands.
 */
function InvoiceSummary({
  engagementId,
  base,
}: {
  engagementId: string;
  base: string;
}) {
  const bills = useResource<BillsView>(
    () => api.get<BillsView>(`/api/engagements/${engagementId}/bills`),
    [engagementId],
  );

  if (bills.resource.state !== "ready") return null;
  const view = bills.resource.data;
  if (view.invoices.length === 0) return null;

  return (
    <section className="mt-12">
      <h2 className="border-t border-line-strong pt-5 font-display text-xl font-medium tracking-title">
        Invoices
      </h2>
      <dl className="mt-4 flex flex-wrap gap-x-10 gap-y-3">
        <div>
          <dt className="label">Outstanding</dt>
          <dd
            data-numeric
            className={`mt-1 text-xl ${view.totals.outstanding > 0 ? "text-ink" : "text-secondary"}`}
          >
            {money(view.totals.outstanding, view.currency)}
          </dd>
        </div>
        {view.totals.overdue > 0 && (
          <div>
            <dt className="label">Overdue</dt>
            <dd data-numeric className="mt-1 text-xl text-danger">
              {money(view.totals.overdue, view.currency)}
            </dd>
          </div>
        )}
      </dl>
      <Link
        to={`${base}/invoices`}
        className="mt-4 inline-block text-base text-secondary underline-offset-4 hover:text-ink hover:underline"
      >
        See all {view.invoices.length} invoice
        {view.invoices.length === 1 ? "" : "s"} &rarr;
      </Link>
    </section>
  );
}

// ================================================================= shared ==

/**
 * One thing waiting on the client's decision.
 *
 * Two different acts against two different tables, each with its own policy
 * since migration 009: approving writes an `approval` row, which
 * `approval_insert_client` admits only when that exact version is published to
 * them and somebody set `requires_client_approval` — so a client cannot
 * approve something nobody asked them to. Asking for changes writes a
 * `revision_request` through `revision_request_client`.
 *
 * Approving is one press and asking for changes is two, which is the right way
 * round: approval is the expected path, and a change request without a reason
 * wastes a week. The note is required here and required by the server.
 */
function Approval({
  item,
  onDone,
}: {
  item: Portal["actionCentre"]["awaitingYourApproval"][number];
  onDone: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState("");

  const approve = useAction(async () => {
    await api.post(`/api/deliverables/${item.deliverableId}/decision`, {
      versionId: item.versionId,
      decision: "approved",
    });
    onDone();
  });

  const askForChanges = useAction(async () => {
    await api.post(`/api/deliverables/${item.deliverableId}/revisions`, {
      versionId: item.versionId,
      body: note.trim(),
    });
    setNote("");
    setAsking(false);
    onDone();
  });

  const refusal = approve.refusal ?? askForChanges.refusal;

  return (
    <div className="register-entry py-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-md text-ink">{item.title}</p>
          <p className="mt-1 text-xs text-muted">
            Version <span className="font-mono">{item.versionNo}</span> &middot;
            ready for your approval
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button tone="secondary" onClick={() => setAsking((v) => !v)}>
            Ask for changes
          </Button>
          <Button
            tone="primary"
            loading={approve.pending}
            onClick={() => void approve.invoke()}
          >
            Approve
          </Button>
        </div>
      </div>

      {asking && (
        <div className="mt-4 border-l-2 border-line pl-4">
          <label className="block text-xs text-muted" htmlFor={`note-${item.versionId}`}>
            What needs to change? Your team sees this as written.
          </label>
          <textarea
            id={`note-${item.versionId}`}
            rows={3}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mt-2 w-full max-w-2xl border border-line bg-transparent px-3 py-2 text-base text-ink outline-none focus:border-ink"
          />
          <div className="mt-3 flex flex-wrap gap-3">
            <Button
              tone="primary"
              disabled={!note.trim()}
              loading={askForChanges.pending}
              onClick={() => void askForChanges.invoke()}
            >
              Send this back
            </Button>
            <Button tone="quiet" onClick={() => setAsking(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {refusal && (
        <p role="alert" className="mt-3 border border-danger/40 bg-danger/[0.08] px-3 py-2 text-xs text-danger">
          {refusal.message}
        </p>
      )}
    </div>
  );
}

/**
 * Fetching a file the client is entitled to.
 *
 * A plain anchor would not do: the download routes are authorised per request
 * and answer 404 rather than 403 when they refuse, so a link would navigate
 * the client away from their portal to a bare error page. This fetches, hands
 * the browser a blob, and keeps any refusal on the screen the person is
 * standing on.
 *
 * Every call writes a `file_access` row through `app.authorise_download`. A
 * client reading their own approved deliverable is logged exactly as a
 * consultant is, which is the point of having the log at all.
 */
function Download({ path, label }: { path: string; label: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function take() {
    setBusy(true);
    setError(null);
    try {
      const file = await api.downloadFile(path);
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoked on the next tick: revoking synchronously races the click in
      // some browsers and the file arrives empty.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "That file could not be downloaded.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="shrink-0">
      <Button
        tone="secondary"
        loading={busy}
        onClick={() => void take()}
        aria-label={`Download ${label}`}
      >
        Download
      </Button>
      {error && (
        <span role="alert" className="ml-3 text-xs text-danger">
          {error}
        </span>
      )}
    </span>
  );
}
